import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scroll the element matching the current URL hash into view on mount and
 * whenever the hash changes.
 *
 * Why this is needed (and the browser's native behaviour isn't enough): a doc
 * page renders its body from an access-gated, lazily-imported chunk, so the
 * heading a deep link targets (`/docs/slug#section`) does not exist in the DOM
 * when the browser — or React Router's `<ScrollRestoration>` — first tries to
 * honour the hash. Nothing scrolls, and with the page parked at the top the
 * scroll-spy reports the wrong active section. This hook lives in the rendered
 * table of contents, so by the time its effect runs the sibling sections are
 * mounted; it (re)applies the scroll once the content is actually present.
 *
 * The sticky-header offset is handled by `scroll-margin-top` on the target
 * (the `scroll-mt-*` utility on each section), which `scrollIntoView` honours, so
 * this hook stays layout-agnostic. Respects `prefers-reduced-motion`.
 * Source: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
 */
export function useHashScroll(): void {
	const { hash } = useLocation();

	useEffect(() => {
		if (!hash) {
			return;
		}
		const raw = hash.replace(/^#/, "");
		if (!raw) {
			return;
		}
		// `useLocation().hash` is user-controlled and may carry malformed percent-
		// escapes (e.g. a truncated "#100%") that make decodeURIComponent throw a
		// URIError. Fall back to the raw fragment so a valid id still matches and a
		// bad one is simply a no-op (getElementById misses) — never a thrown error
		// that breaks the render.
		let id: string;
		try {
			id = decodeURIComponent(raw);
		} catch {
			id = raw;
		}
		// Defer one frame so a just-mounted target is laid out, and so this runs
		// after `<ScrollRestoration>`'s synchronous scroll-to-top on navigation.
		const frame = requestAnimationFrame(() => {
			const target = document.getElementById(id);
			if (!target) {
				return;
			}
			const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
			target.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
		});
		return () => cancelAnimationFrame(frame);
	}, [hash]);
}
